import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { Ad } from '@/lib/types';

interface KeyWord {
  keyword: string;
  googleads_ad_group_id: string;
  impressions: number;
  clicks: number;
  conversions: number;
  cost: number;
  ctr: number;
  cvr: number;
  ad_group_id: number;
  matched_rules: Array<{
    confidence_level: string;
    priority: number | string;
    tags: string[];
    reason: string;
    campaign_type: string;
  }>;
  rule_results?: Record<string, { reason: string }>;
}

interface KeywordsProps {
  keywords: KeyWord[];
  isLoading: boolean;
  error: string | null;
  selectedTerms: Set<string>;
  selectAllChecked: boolean;
  onTermSelect: (term: KeyWord) => void;
  onSelectAll: (checked: boolean) => void;
  campaignAds: Ad[];
}

export const Keywords = ({
  keywords,
  isLoading,
  error,
  selectedTerms,
  selectAllChecked,
  onTermSelect,
  onSelectAll,
}: KeywordsProps) => {
  const { symbol: currencySymbol } = useCurrencySymbol('google');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
        <span className="ml-2">Loading keywords data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-destructive/10 text-destructive rounded-md">
        <p className="font-medium">Error loading data</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (!keywords.length) {
    return (
      <div className="text-center p-8 bg-muted rounded-md">
        <p className="text-lg font-medium mb-2">No keywords data available</p>
        <p className="text-sm text-muted-foreground">
          No keyword candidates were found matching the current rules.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-medium">Keywords</h3>
          <Badge variant="outline">{keywords.length} total</Badge>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="border-r border-gray-200">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectAllChecked}
                    onChange={(e) => onSelectAll(e.target.checked)}
                    className="h-4 w-4 mr-2 text-blue-600 border-gray-300 rounded"
                    style={{ width: '16px', height: '16px', minWidth: '16px', minHeight: '16px' }}
                    title="Select all keywords"
                  />
                  Keyword
                </div>
              </TableHead>
              <TableHead className="w-[100px] text-right">Ad Group Id.</TableHead>
              <TableHead className="w-[100px] text-right">Impr.</TableHead>
              <TableHead className="w-[80px] text-right">Clicks</TableHead>
              <TableHead className="w-[80px] text-right">Conv.</TableHead>
              <TableHead className="w-[80px] text-right border-l border-gray-200">Cost</TableHead>
              <TableHead className="w-[80px] text-right">CPC</TableHead>
              <TableHead className="w-[80px] text-right">CTR</TableHead>
              <TableHead className="w-[80px] text-right">CVR</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keywords.map((term, index) => {
              const rowClass = 'bg-red-50';

              const termKey = `${term.keyword}-${term.googleads_ad_group_id}`;
              const isSelected = selectedTerms.has(termKey);

              return (
                <TableRow 
                  key={`${term.keyword}-${term.googleads_ad_group_id}-${index}`} 
                  className={`${rowClass} ${'cursor-pointer'}`}
                  onClick={() => onTermSelect(term)}
                >
                  <TableCell className="font-medium border-r border-gray-200">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          onTermSelect(term);
                        }}
                        className={`h-4 w-4 mr-2 text-blue-600 border-gray-300 rounded`}
                        style={{ width: '16px', height: '16px', minWidth: '16px', minHeight: '16px' }}
                        title={"Select this keyword"}
                      />
                      {term.keyword}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{term.ad_group_id}</TableCell>
                  <TableCell className="text-right">{term.impressions.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{term.clicks.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{term.conversions.toLocaleString()}</TableCell>
                  <TableCell className="text-right border-l border-gray-200">{currencySymbol}{term.cost.toFixed(2)}</TableCell>
                  <TableCell className="text-right">{term.clicks > 0 ? `${currencySymbol}${(term.cost / term.clicks).toFixed(2)}` : '-'}</TableCell>
                  <TableCell className="text-right">{(term.ctr * 100).toFixed(2)}%</TableCell>
                  <TableCell className="text-right">{(term.cvr * 100).toFixed(2)}%</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}; 